import React, { useMemo } from 'react';
import { FormItem, EditorInput, DatePicker } from 'components/elements';
import { Input, Select, Row, Col, InputNumber, Form, Switch } from 'antd';
import { useGetEventTimezonesQuery, useGetMyAccountQuery } from 'graphql/types';
import { eventDateTimeFormat, eventHourFormat } from 'settings/configs';
import { getDefaultEventTimezone, mapEventTimezoneOptions } from 'utils/eventDateTime';
import {
  EventDateTimeRangePickerStyles,
  RecurringControlsRow,
  RecurringDayInputRow,
  RecurringSection,
} from './informationStep.styled';
import { defaultRecurringPattern, RecurrenceTypeEnum, recurringPatternOptions } from './recurringOptions';

const { RangePicker } = DatePicker;

function InformationStep() {
  const { data: myAccount } = useGetMyAccountQuery();
  const { data: timezoneData, loading: timezonesLoading } = useGetEventTimezonesQuery({
    variables: { includeAll: false },
  });
  const clientTimezone = getDefaultEventTimezone();

  const options = useMemo(() => {
    const attributes = myAccount?.myAccount?.data?.attributes;
    const allGroups = [
      ...(attributes?.ledGroups?.data ?? []),
      ...(attributes?.groups?.data ?? []),
      ...(attributes?.managedGroups?.data ?? []),
    ];

    const seen = new Set<string>();
    return allGroups.reduce<{ value: string; label?: string }[]>((acc, group) => {
      if (group?.id && !seen.has(group.id)) {
        seen.add(group.id);
        acc.push({ value: group.id, label: group.attributes?.name });
      }
      return acc;
    }, []);
  }, [myAccount]);

  const eventTimezones = timezoneData?.eventTimezones;
  const timezoneOptions = useMemo(
    () => mapEventTimezoneOptions(eventTimezones ?? [], clientTimezone),
    [clientTimezone, eventTimezones],
  );

  return (
    <>
      <EventDateTimeRangePickerStyles />
      <FormItem
        name="title"
        label="Title"
        theme="primary"
        rules={[
          {
            required: true,
            message: 'Please input your Title!',
          },
        ]}
      >
        <Input placeholder="Enter Your Event Title" />
      </FormItem>
      <Row gutter={12}>
        <Col xs={24} md={10}>
          <FormItem
            name="group"
            label="Select Your Group"
            theme="primary"
            rules={[
              {
                required: true,
                message: 'Please Select Your Group!',
              },
            ]}
          >
            <Select options={options} />
          </FormItem>
        </Col>
        <Col xs={24} md={14}>
          <FormItem
            name="dates"
            label="Select Your Start and End Date Time"
            theme="primary"
            rules={[
              {
                required: true,
                message: 'Please Select Your Dates!',
              },
            ]}
          >
            <RangePicker
              popupClassName="event-datetime-range-popup"
              showTime={{ minuteStep: 15, format: eventHourFormat }}
              format={eventDateTimeFormat}
              placeholder={['Start date and time', 'End date and time']}
            />
          </FormItem>
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          <FormItem
            name="timezone"
            label="Time Zone"
            theme="primary"
            initialValue={clientTimezone}
            rules={[{ required: true, message: 'Please select a time zone!' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              loading={timezonesLoading}
              options={timezoneOptions}
            />
          </FormItem>
          <div style={{ marginTop: -6, marginBottom: 12, color: '#667085', fontSize: 12 }}>
            The times above are interpreted in the selected time zone.
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          <FormItem label="Recurring Option" theme="primary">
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.repeatDayInterval !== currentValues.repeatDayInterval ||
                prevValues.recurringPattern !== currentValues.recurringPattern
              }
            >
              {({ getFieldValue, setFieldsValue }) => {
                const repeatDayInterval = getFieldValue('repeatDayInterval') ?? 0;
                const recurringPattern = getFieldValue('recurringPattern') ?? defaultRecurringPattern;
                const isOneTime = !repeatDayInterval;
                const showDayInterval = !isOneTime && recurringPattern === RecurrenceTypeEnum.RepeatDays;

                const toggleRecurring = () => {
                  if (isOneTime) {
                    setFieldsValue({
                      repeatDayInterval: 1,
                      recurringPattern: defaultRecurringPattern,
                    });
                    return;
                  }
                  setFieldsValue({ repeatDayInterval: 0 });
                };

                return (
                  <RecurringSection>
                    <RecurringControlsRow>
                      <div className="recurring-switch-wrap">
                        <Switch
                          checked={isOneTime}
                          checkedChildren="One-Time Event"
                          unCheckedChildren="Recurring"
                          onChange={toggleRecurring}
                        />
                      </div>
                      {!isOneTime && (
                        <div className="recurring-pattern-wrap">
                          <FormItem
                            name="recurringPattern"
                            theme="primary"
                            noStyle
                            initialValue={defaultRecurringPattern}
                          >
                            <Select
                              placeholder="Select recurring pattern"
                              options={recurringPatternOptions}
                              style={{ width: '100%' }}
                            />
                          </FormItem>
                        </div>
                      )}
                    </RecurringControlsRow>
                    {showDayInterval && (
                      <RecurringDayInputRow>
                        <FormItem
                          name="repeatDayInterval"
                          theme="primary"
                          noStyle
                          rules={[{ required: true, message: 'Please enter repeat interval.' }]}
                        >
                          <InputNumber
                            min={1}
                            addonBefore="Repeat every"
                            addonAfter="Day"
                            style={{ width: '100%' }}
                          />
                        </FormItem>
                      </RecurringDayInputRow>
                    )}
                  </RecurringSection>
                );
              }}
            </Form.Item>
          </FormItem>
        </Col>
      </Row>
      <FormItem
        name="description"
        label="Description"
        theme="primary"
        fullRounded={false}
        rules={[
          {
            required: true,
            message: 'Please input your Description!',
          },
        ]}
      >
        <EditorInput />
      </FormItem>
    </>
  );
}

export default InformationStep;
